import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from '../../../context/SnackbarContext';
import {
  useCampaignId,
  useSpreadsheetViewType,
  useCampaignContextController,
  setCrmSyncActive
} from '../../../context/CampaignContext';
import { useUserContextController } from '../../../context/UserContext';
import { CampaignProspectType } from '../../../api/campaigns';
import {
  getColumns,
  getCells,
  Column,
  displayColumnName
} from '../../../api/spreadsheet';
import { getCrmMapping, upsertCrmMapping } from '../../../api/crmMapping';
import {
  getSalesforceSchemaForMapping,
  previewSalesforceMappedUpsert,
  startSalesforceMappedUpsertJob,
  getSalesforceMappedUpsertJobStatus,
  getSalesforceMappedUpsertJobResult
} from '../../../api/connector/salesforce';
import {
  getHubSpotSchemaForMapping,
  startHubSpotSearchJob,
  startHubSpotSync,
  getHubSpotJobStatus,
  getHubSpotJobResult,
  validateHubSpotMapping
} from '../../../api/connector/hubspot';
import {
  SalesforceFieldForMapping,
  SalesforceTargetObjectName,
  NormalizeRule,
  FieldCategory,
  LookupSearchBy
} from '../../../types/salesforce';
import {
  formatValueForCrmDate,
  formatValueForCrmDatetime,
  rowsWithEmptyCellsOmitted
} from '../../../utils/crmSyncDateUtils';
import {
  mappedSalesforceAccountNameSourceColumn,
  salesforceFieldEligibleForPrimaryKey,
  salesforceMappedTargetsIncludeReadOnlySearchField,
  spreadsheetCellIsBlankForCrmSync
} from '../../../utils/salesforceMappingUtils';
import {
  formatCompositePkDisplay,
  normalizedCompositeKeyFromRow,
  orderedPkSourceColumns,
  pkSourceColumnsFromConnectorFields,
  rowHasAllPkValues
} from '../../../utils/crmCompositePrimaryKey';
import type {
  MappingConfig as SalesforceMappingConfig,
  SalesforceInputDuplicateGroup,
  SalesforcePreviewConflictResponse,
  UserConfirmedLookups,
  UpsertMappedPreviewResponse,
  ValidationSummary as SalesforceValidationSummaryWire
} from '../../../types/salesforce';
import {
  HubSpotFieldForMapping,
  HubSpotTargetObjectName,
  ValidationSummary as HubSpotValidationSummary
} from '../../../types/hubspot';
import type {
  MappingConfig,
  UserConfirmedSelections,
  HubSpotSearchResponse
} from '../../../types/hubspot';
import { URL_PATHNAMES } from '../../../components/routes/AppRoutes';
import CRMMappingHeader from './CRMMappingHeader';
import TargetObjectSelector, {
  CrmProvider,
  CampaignType,
  getValidTargetValuesForCrm
} from './TargetObjectSelector';
import MappingTable, { ColumnMapping } from './MappingTable';
import EmptyState from './EmptyState';
import EditModeActions from './EditModeActions';
import SyncButton from './SyncButton';
import SyncConflictModal, {
  type DuplicateRowHintField
} from './SyncConflictModal';

function initUserConfirmedLookups(
  suggestions: UpsertMappedPreviewResponse['lookup_suggestions']
): UserConfirmedLookups {
  if (!suggestions) return {};
  const out: UserConfirmedLookups = {};
  for (const [field, byValue] of Object.entries(suggestions)) {
    if (!byValue) continue;
    out[field] = {};
    for (const [sourceValue, list] of Object.entries(byValue)) {
      if (!list?.length) {
        out[field][sourceValue] = null;
        continue;
      }
      const exact = list.find((s) => s.exact_match);
      out[field][sourceValue] = exact?.id ?? list[0].id;
    }
  }
  return out;
}

interface CRMMappingProps {
  onClose: () => void;
  /** Row indices currently hidden by the sheet filter; sync will exclude these rows. */
  getFilteredOutRowIds?: () => number[] | null;
}

function cloneColumnMappings(rows: ColumnMapping[]): ColumnMapping[] {
  return rows.map((m) => ({ ...m }));
}

interface MappingEditBaseline {
  targetObject: string | null;
  selectedCrm: CrmProvider;
  mappings: ColumnMapping[];
}

const CRMMapping: React.FC<CRMMappingProps> = ({
  onClose,
  getFilteredOutRowIds
}) => {
  const campaignId = useCampaignId();
  const viewType = useSpreadsheetViewType();
  const { showSnackbar } = useSnackbar();
  const [userContext] = useUserContextController();
  const [campaignState, dispatch] = useCampaignContextController();

  const campaignType: CampaignType | null =
    campaignState.currentCampaign?.prospect_type ===
    CampaignProspectType.ACCOUNT
      ? 'account'
      : campaignState.currentCampaign?.prospect_type ===
          CampaignProspectType.OPPORTUNITY
        ? 'opportunity'
        : null;

  const hasSalesforce = !!userContext?.connectedAccounts?.some(
    (a) => a.app === 'salesforce_rest_api' && !!a.connectionId
  );
  const hasHubspot = !!userContext?.connectedAccounts?.some(
    (a) => a.app === 'hubspot' && !!a.connectionId
  );
  const hasCrmConnected = hasSalesforce || hasHubspot;
  const isAdmin = !!userContext?.user?.isAdmin;
  const bothCrmConnected = hasSalesforce && hasHubspot;

  const [selectedCrm, setSelectedCrm] = useState<CrmProvider>('salesforce');
  const activeCrm: CrmProvider = useMemo(() => {
    if (bothCrmConnected) return selectedCrm;
    if (hasSalesforce) return 'salesforce';
    return 'hubspot';
  }, [bothCrmConnected, hasSalesforce, hasHubspot, selectedCrm]);

  const [targetObject, setTargetObject] = useState<string | null>(null);
  const [tableColumns, setTableColumns] = useState<Column[]>([]);
  const [crmFields, setCrmFields] = useState<
    (SalesforceFieldForMapping | HubSpotFieldForMapping)[]
  >([]);
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [loadingCrmFields, setLoadingCrmFields] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<{
    processed: number;
    created: number;
    updated: number;
    skipped: number;
    failed: number;
  } | null>(null);
  const [mappingEditModalOpen, setMappingEditModalOpen] = useState(false);
  const [editingMappings, setEditingMappings] = useState<ColumnMapping[]>([]);
  const [saving, setSaving] = useState(false);
  const [syncConflictOpen, setSyncConflictOpen] = useState(false);
  const [searchResult, setSearchResult] = useState<
    HubSpotSearchResponse | SalesforcePreviewConflictResponse | null
  >(null);
  const [pendingMapping, setPendingMapping] = useState<MappingConfig | null>(
    null
  );
  const [pendingRows, setPendingRows] = useState<Record<string, any>[] | null>(
    null
  );
  const [conflictSelections, setConflictSelections] = useState<
    Record<string, string | null>
  >({});
  const [hubspotValidationSummary, setHubspotValidationSummary] =
    useState<HubSpotValidationSummary | null>(null);
  const [pendingSalesforceMapping, setPendingSalesforceMapping] =
    useState<SalesforceMappingConfig | null>(null);
  const [pendingSalesforceRows, setPendingSalesforceRows] = useState<
    Record<string, any>[] | null
  >(null);
  const [
    pendingSalesforceLookupSuggestions,
    setPendingSalesforceLookupSuggestions
  ] = useState<UpsertMappedPreviewResponse['lookup_suggestions']>(null);
  const [salesforceLookupSelections, setSalesforceLookupSelections] =
    useState<UserConfirmedLookups>({});
  const [
    pendingSalesforceInputDuplicateGroups,
    setPendingSalesforceInputDuplicateGroups
  ] = useState<SalesforceInputDuplicateGroup[] | null>(null);
  const [
    salesforceInputDuplicateSelections,
    setSalesforceInputDuplicateSelections
  ] = useState<Record<string, number>>({});

  /** Validation started when sync is pressed (HubSpot); awaited before opening conflict modal. */
  const hubspotValidationPromiseRef = useRef<Promise<{
    validation_summary: HubSpotValidationSummary;
  }> | null>(null);

  /** Committed sidebar state before opening Edit mapping — restored on discard close. */
  const mappingEditBaselineRef = useRef<MappingEditBaseline | null>(null);

  // Lock spreadsheet while CRM sync is in progress
  useEffect(() => {
    setCrmSyncActive(dispatch, syncing);
    return () => setCrmSyncActive(dispatch, false);
  }, [syncing, dispatch]);

  // Fetch table columns and CRM mapping on mount
  useEffect(() => {
    if (!campaignId) return;

    const fetchData = async () => {
      setLoadingColumns(true);
      try {
        const [columnsResponse, crmMappingResponse] = await Promise.all([
          getColumns(campaignId, viewType),
          getCrmMapping(campaignId, viewType).catch(() => ({
            crm_mapping: null
          }))
        ]);
        const columns = (columnsResponse.columns || []).sort(
          (a: Column, b: Column) => (a.col_idx ?? 0) - (b.col_idx ?? 0)
        );
        setTableColumns(columns);

        const baseMappings: ColumnMapping[] = columns.map((col: Column) => ({
          tableColumn: col.col_name,
          crmField: null,
          allowOverwrite: false,
          isPrimaryKey: false
        }));

        const saved = crmMappingResponse.crm_mapping;
        if (saved?.target_object) {
          const crmFromSaved: CrmProvider =
            saved.target_object === HubSpotTargetObjectName.COMPANY ||
            saved.target_object === HubSpotTargetObjectName.CONTACT
              ? 'hubspot'
              : 'salesforce';
          const savedCrmIsConnected =
            (crmFromSaved === 'hubspot' && hasHubspot) ||
            (crmFromSaved === 'salesforce' && hasSalesforce);

          if (!savedCrmIsConnected) {
            setTargetObject(null);
            setMappings(baseMappings);
            setEditingMappings(baseMappings);
          } else {
            const prospectTypeFromCampaign: CampaignType | null =
              campaignState.currentCampaign?.prospect_type ===
              CampaignProspectType.ACCOUNT
                ? 'account'
                : campaignState.currentCampaign?.prospect_type ===
                    CampaignProspectType.OPPORTUNITY
                  ? 'opportunity'
                  : null;
            const allowed = getValidTargetValuesForCrm(
              crmFromSaved,
              prospectTypeFromCampaign,
              viewType
            );
            setSelectedCrm(crmFromSaved);
            if (allowed.has(saved.target_object)) {
              setTargetObject(saved.target_object);
              const merged = baseMappings.map((m) => {
                const crmField = saved.field_mappings?.[m.tableColumn] ?? null;
                const isPrimaryKey =
                  saved.dedup_fields?.includes(m.tableColumn) ?? false;
                const allowOverwrite =
                  saved.overwrite_on_dedup?.includes(m.tableColumn) ?? false;
                const savedLookupPref =
                  crmFromSaved === 'salesforce'
                    ? saved.salesforce_lookup_search_by?.[m.tableColumn]
                    : undefined;
                return {
                  ...m,
                  crmField,
                  isPrimaryKey,
                  allowOverwrite,
                  ...(savedLookupPref != null && {
                    lookupSearchBy:
                      savedLookupPref === LookupSearchBy.ID
                        ? LookupSearchBy.ID
                        : LookupSearchBy.NAME
                  })
                };
              });
              setMappings(merged);
              setEditingMappings(merged);
            } else {
              setMappings(baseMappings);
              setEditingMappings(baseMappings);
            }
          }
        } else {
          setMappings(baseMappings);
          setEditingMappings(baseMappings);
        }
      } catch (error: any) {
        showSnackbar(error?.message || 'Failed to load table columns', 'error');
      } finally {
        setLoadingColumns(false);
      }
    };

    fetchData();
  }, [
    campaignId,
    viewType,
    campaignState.spreadsheetDataVersion,
    showSnackbar,
    hasHubspot,
    hasSalesforce
  ]);

  const validTargetValues = useMemo(
    () => getValidTargetValuesForCrm(activeCrm, campaignType, viewType),
    [activeCrm, campaignType, viewType]
  );

  // When active CRM or campaign type changes, clear target object if it is not in the allowed set
  useEffect(() => {
    if (targetObject && !validTargetValues.has(targetObject)) {
      setTargetObject(null);
      const resetMappings = tableColumns.map((col) => ({
        tableColumn: col.col_name,
        crmField: null,
        allowOverwrite: false,
        isPrimaryKey: false
      }));
      setMappings(resetMappings);
      setEditingMappings(resetMappings);
      setCrmFields([]);
    }
  }, [activeCrm, campaignType, targetObject, tableColumns, validTargetValues]);

  // Clear sync/conflict state when mapping config changes so we never use stale payloads
  useEffect(() => {
    setSyncConflictOpen(false);
    setSearchResult(null);
    setPendingMapping(null);
    setPendingRows(null);
    setConflictSelections({});
    setPendingSalesforceMapping(null);
    setPendingSalesforceRows(null);
    setPendingSalesforceLookupSuggestions(null);
    setSalesforceLookupSelections({});
    setPendingSalesforceInputDuplicateGroups(null);
    setSalesforceInputDuplicateSelections({});
  }, [targetObject, mappings]);

  // Fetch CRM fields when target object is selected
  useEffect(() => {
    if (!targetObject) {
      setCrmFields([]);
      return;
    }

    const fetchCrmFields = async () => {
      setLoadingCrmFields(true);
      try {
        if (activeCrm === 'salesforce') {
          const response = await getSalesforceSchemaForMapping(
            targetObject as SalesforceTargetObjectName
          );
          setCrmFields(response.fields || []);
        } else {
          const response = await getHubSpotSchemaForMapping(
            targetObject as HubSpotTargetObjectName
          );
          setCrmFields(response.fields || []);
        }
      } catch (error: any) {
        showSnackbar(error?.message || 'Failed to load CRM fields', 'error');
        setCrmFields([]);
      } finally {
        setLoadingCrmFields(false);
      }
    };

    fetchCrmFields();
  }, [targetObject, activeCrm, showSnackbar]);

  const handleMappingChange = (
    tableColumn: string,
    crmField: string | null
  ) => {
    setEditingMappings((prev) => {
      if (crmField === null) {
        return prev.map((mapping) =>
          mapping.tableColumn === tableColumn
            ? {
                ...mapping,
                crmField: null,
                lookupSearchBy: undefined,
                isPrimaryKey: false
              }
            : mapping
        );
      }

      return prev.map((mapping) => {
        if (
          mapping.tableColumn !== tableColumn &&
          mapping.crmField === crmField
        ) {
          return {
            ...mapping,
            crmField: null,
            lookupSearchBy: undefined,
            isPrimaryKey: false,
            allowOverwrite: false
          };
        }
        if (mapping.tableColumn !== tableColumn) return mapping;

        const fieldMeta = crmFields.find((f) => f.apiName === crmField) as
          | SalesforceFieldForMapping
          | undefined;
        const pkOk =
          activeCrm !== 'salesforce' ||
          salesforceFieldEligibleForPrimaryKey(fieldMeta);
        if (
          activeCrm === 'salesforce' &&
          fieldMeta?.fieldCategory === FieldCategory.LOOKUP
        ) {
          return {
            ...mapping,
            crmField,
            lookupSearchBy:
              (mapping.crmField === crmField
                ? mapping.lookupSearchBy
                : undefined) ?? LookupSearchBy.NAME,
            isPrimaryKey: pkOk ? mapping.isPrimaryKey : false
          };
        }
        return {
          ...mapping,
          crmField,
          lookupSearchBy: undefined,
          isPrimaryKey: pkOk ? mapping.isPrimaryKey : false
        };
      });
    });
  };

  const handleLookupSearchByChange = (
    tableColumn: string,
    value: LookupSearchBy
  ) => {
    setEditingMappings((prev) =>
      prev.map((mapping) =>
        mapping.tableColumn === tableColumn
          ? { ...mapping, lookupSearchBy: value }
          : mapping
      )
    );
  };

  const handleAllowOverwriteChange = (
    tableColumn: string,
    allowOverwrite: boolean
  ) => {
    setEditingMappings((prev) =>
      prev.map((mapping) =>
        mapping.tableColumn === tableColumn
          ? { ...mapping, allowOverwrite }
          : mapping
      )
    );
  };

  const handlePrimaryKeyChange = (tableColumn: string, checked: boolean) => {
    setEditingMappings((prev) =>
      prev.map((mapping) => {
        if (mapping.tableColumn !== tableColumn) return mapping;
        if (checked && activeCrm === 'salesforce' && mapping.crmField) {
          const meta = crmFields.find((f) => f.apiName === mapping.crmField) as
            | SalesforceFieldForMapping
            | undefined;
          if (!salesforceFieldEligibleForPrimaryKey(meta)) return mapping;
        }
        return { ...mapping, isPrimaryKey: checked };
      })
    );
  };

  const handleMappingEditModalClose = () => {
    const baseline = mappingEditBaselineRef.current;
    if (baseline) {
      setTargetObject(baseline.targetObject);
      setSelectedCrm(baseline.selectedCrm);
      const restored = cloneColumnMappings(baseline.mappings);
      setMappings(restored);
      setEditingMappings(restored);
      mappingEditBaselineRef.current = null;
    } else {
      setEditingMappings([...mappings]);
    }
    setMappingEditModalOpen(false);
  };

  const handleEdit = () => {
    mappingEditBaselineRef.current = {
      targetObject,
      selectedCrm,
      mappings: cloneColumnMappings(mappings)
    };
    setEditingMappings(cloneColumnMappings(mappings));
    setMappingEditModalOpen(true);
  };

  const handleCancel = () => {
    handleMappingEditModalClose();
  };

  const handleSave = async () => {
    if (!campaignId || !targetObject) return;

    const needsCrmForPkOrOverwrite = editingMappings.filter(
      (m) => (m.isPrimaryKey || m.allowOverwrite) && !m.crmField
    );
    if (needsCrmForPkOrOverwrite.length > 0) {
      const labels = needsCrmForPkOrOverwrite.map((m) =>
        displayColumnName(m.tableColumn)
      );
      showSnackbar(
        labels.length === 1
          ? `Select a CRM field for "${labels[0]}" — primary key and overwrite require a mapped field.`
          : `Select CRM fields for columns using primary key or overwrite: ${labels.join(', ')}.`,
        'warning'
      );
      return;
    }

    if (!editingMappings.some((m) => m.isPrimaryKey)) {
      showSnackbar(
        'Select at least one primary key column in the mapping to check for existing CRM records and avoid duplicates.',
        'warning'
      );
      return;
    }

    if (activeCrm === 'salesforce') {
      const invalidPk = editingMappings.filter(
        (m) =>
          m.isPrimaryKey &&
          m.crmField &&
          !salesforceFieldEligibleForPrimaryKey(
            crmFields.find((f) => f.apiName === m.crmField) as
              | SalesforceFieldForMapping
              | undefined
          )
      );
      if (invalidPk.length > 0) {
        showSnackbar(
          'Primary key must use a filterable Salesforce field (not long text, rich text, encrypted text, or similar).',
          'warning'
        );
        return;
      }
    }

    setSaving(true);
    try {
      const field_mappings: Record<string, string> = {};
      const dedup_fields: string[] = [];
      const overwrite_on_dedup: string[] = [];

      for (const m of editingMappings) {
        if (m.crmField) {
          field_mappings[m.tableColumn] = m.crmField;
          if (m.isPrimaryKey) dedup_fields.push(m.tableColumn);
          if (m.allowOverwrite) overwrite_on_dedup.push(m.tableColumn);
        }
      }

      const prefs: Record<string, LookupSearchBy> = {};
      if (activeCrm === 'salesforce') {
        for (const m of editingMappings) {
          if (!m.crmField) continue;
          const fieldMeta = crmFields.find((f) => f.apiName === m.crmField) as
            | SalesforceFieldForMapping
            | undefined;
          if (fieldMeta?.fieldCategory === FieldCategory.LOOKUP) {
            prefs[m.tableColumn] = m.lookupSearchBy ?? LookupSearchBy.NAME;
          }
        }
      }

      await upsertCrmMapping(
        campaignId,
        {
          target_object: targetObject,
          field_mappings,
          dedup_fields,
          overwrite_on_dedup,
          ...(activeCrm === 'salesforce'
            ? { salesforce_lookup_search_by: prefs }
            : {})
        },
        viewType
      );
      setMappings(cloneColumnMappings(editingMappings));
      mappingEditBaselineRef.current = null;
      setMappingEditModalOpen(false);
      showSnackbar('Mapping configuration saved', 'success');
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to save mapping', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTargetObjectChange = (value: string) => {
    setTargetObject(value || null);
    const resetMappings = tableColumns.map((col) => ({
      tableColumn: col.col_name,
      crmField: null,
      allowOverwrite: false,
      isPrimaryKey: false
    }));
    setMappings(resetMappings);
    setEditingMappings(resetMappings);
  };

  const pollJobUntilFinished = async (jobId: string) => {
    const pollIntervalMs = 1500;
    while (true) {
      const status = await getHubSpotJobStatus(jobId);
      if (status.status === 'finished') return status;
      if (status.status === 'failed') {
        throw new Error(status.error || 'Job failed');
      }
      await new Promise((r) => setTimeout(r, pollIntervalMs));
    }
  };

  const runSyncWithSelections = async (
    mapping: MappingConfig,
    rows: Record<string, any>[],
    user_confirmed_selections: UserConfirmedSelections
  ) => {
    setSyncing(true);
    setSyncProgress(null);
    try {
      const { job_id } = await startHubSpotSync({
        mapping,
        rows: rowsWithEmptyCellsOmitted(rows),
        user_confirmed_selections
      });
      const pollIntervalMs = 1500;
      while (true) {
        const status = await getHubSpotJobStatus(job_id);
        if (status.progress) {
          setSyncProgress({
            processed: status.progress.processed,
            created: status.progress.created,
            updated: status.progress.updated,
            skipped: status.progress.skipped,
            failed: status.progress.failed
          });
        }
        if (status.status === 'finished') break;
        if (status.status === 'failed') {
          throw new Error(status.error || 'Sync failed');
        }
        await new Promise((r) => setTimeout(r, pollIntervalMs));
      }
      const result = await getHubSpotJobResult(job_id);
      if (
        result &&
        typeof result === 'object' &&
        'created' in result &&
        'updated' in result
      ) {
        const { created, updated, failed } = result;
        const parts = [];
        if (created > 0) parts.push(`${created} created`);
        if (updated > 0) parts.push(`${updated} updated`);
        if (failed > 0) parts.push(`${failed} failed`);
        showSnackbar(
          parts.length > 0
            ? `Sync complete: ${parts.join(', ')}`
            : 'Sync complete.',
          failed > 0 ? 'warning' : 'success'
        );
      } else {
        showSnackbar('Sync complete', 'success');
      }
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to sync to CRM', 'error');
    } finally {
      setSyncing(false);
      setSyncProgress(null);
    }
  };

  const runSalesforceSync = async (
    mapping: SalesforceMappingConfig,
    rows: Record<string, any>[],
    user_confirmed_selections?: UserConfirmedSelections | null,
    user_confirmed_lookups?: UserConfirmedLookups | null
  ) => {
    setSyncing(true);
    setSyncProgress(null);
    try {
      const { job_id } = await startSalesforceMappedUpsertJob({
        mapping,
        rows,
        user_confirmed_selections: user_confirmed_selections ?? undefined,
        user_confirmed_lookups: user_confirmed_lookups ?? undefined
      });
      const pollIntervalMs = 1500;
      while (true) {
        const status = await getSalesforceMappedUpsertJobStatus(job_id);
        if (status.progress) {
          setSyncProgress({
            processed: status.progress.processed ?? 0,
            created: status.progress.created ?? 0,
            updated: status.progress.updated ?? 0,
            skipped: status.progress.skipped ?? 0,
            failed: status.progress.errors ?? 0
          });
        }
        if (status.status === 'finished') break;
        if (status.status === 'failed') {
          throw new Error(status.error || 'Sync failed');
        }
        await new Promise((r) => setTimeout(r, pollIntervalMs));
      }
      const result = await getSalesforceMappedUpsertJobResult(job_id);
      if (
        result &&
        typeof result === 'object' &&
        'created' in result &&
        'updated' in result
      ) {
        const { created, updated, failed } = result;
        const parts = [];
        if (created > 0) parts.push(`${created} created`);
        if (updated > 0) parts.push(`${updated} updated`);
        if (failed > 0) parts.push(`${failed} failed`);
        showSnackbar(
          parts.length > 0
            ? `Sync complete: ${parts.join(', ')}`
            : 'Sync complete.',
          failed > 0 ? 'warning' : 'success'
        );
      } else {
        showSnackbar('Sync complete', 'success');
      }
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to sync to CRM', 'error');
    } finally {
      setSyncing(false);
      setSyncProgress(null);
    }
  };

  const buildConflictSyncPayload = (args: {
    searchResult: HubSpotSearchResponse | SalesforcePreviewConflictResponse;
    rows: Record<string, any>[];
    /**
     * Salesforce only: mapped read-only/filterable columns allow sync rows whose spreadsheet
     * primary-column cell is empty. This applies to single-column PKs; composite PK
     * rows must still have all PK values present.
     *
     * Should only be true for read-only Salesforce fields, other fields should not allow blank primary keys.
     */
    salesforceBlankPkAllowedWhenReadOnlySearch?: boolean;
    pkSourceColumns: string[];
    /** 0-based row indices excluded from sync (spreadsheet-dupe resolution). */
    excludeRowIndices?: ReadonlySet<number>;
  }) => {
    const {
      searchResult,
      rows,
      pkSourceColumns,
      salesforceBlankPkAllowedWhenReadOnlySearch = false,
      excludeRowIndices
    } = args;

    const user_confirmed_selections: UserConfirmedSelections = {};
    searchResult.not_found.forEach((key) => {
      user_confirmed_selections[key] = null;
    });
    Object.keys(searchResult.results).forEach((key) => {
      const choice = conflictSelections[key];
      if (typeof choice === 'string' && choice) {
        user_confirmed_selections[key] = choice;
      }
    });

    const ignoredKeys = new Set(
      Object.keys(searchResult.results).filter(
        (key) =>
          !(
            typeof conflictSelections[key] === 'string' &&
            conflictSelections[key]
          )
      )
    );
    const rowsToSync = rows.filter((row, idx) => {
      if (excludeRowIndices?.has(idx)) return false;
      const hasAllPk = rowHasAllPkValues(
        row as Record<string, unknown>,
        pkSourceColumns
      );
      if (!hasAllPk) {
        // Salesforce read-only search fields may allow blank single-column PK.
        if (
          salesforceBlankPkAllowedWhenReadOnlySearch &&
          pkSourceColumns.length === 1
        ) {
          return true;
        }
        return false;
      }
      const nk = normalizedCompositeKeyFromRow(
        row as Record<string, unknown>,
        pkSourceColumns
      );
      if (ignoredKeys.has(nk)) return false;
      return true;
    });
    return { user_confirmed_selections, rowsToSync };
  };

  const handleConflictConfirm = () => {
    if (!searchResult) return;

    if (pendingSalesforceMapping && pendingSalesforceRows) {
      const pkCols = pkSourceColumnsFromConnectorFields(
        pendingSalesforceMapping.fields
      );
      const sfBlankPkAllowed =
        salesforceMappedTargetsIncludeReadOnlySearchField(
          pendingSalesforceMapping.fields.map((f) => f.target_field_api_name),
          crmFields as SalesforceFieldForMapping[]
        );
      const spreadsheetDupExcludeIndices = (() => {
        const groups = pendingSalesforceInputDuplicateGroups;
        if (!groups?.length) return undefined;
        const excluded = new Set<number>();
        for (const g of groups) {
          const sorted = [...g.row_indices].sort((a, b) => a - b);
          const chosen =
            salesforceInputDuplicateSelections[g.normalized_primary_key] ??
            sorted[0];
          const pick = sorted.includes(chosen) ? chosen : sorted[0];
          for (const idx of g.row_indices) {
            if (idx !== pick) excluded.add(idx);
          }
        }
        return excluded.size > 0 ? excluded : undefined;
      })();
      const { user_confirmed_selections, rowsToSync } =
        buildConflictSyncPayload({
          searchResult,
          rows: pendingSalesforceRows,
          pkSourceColumns: pkCols,
          salesforceBlankPkAllowedWhenReadOnlySearch:
            sfBlankPkAllowed && pkCols.length === 1,
          excludeRowIndices: spreadsheetDupExcludeIndices
        });

      const userConfirmedLookupsForSync: UserConfirmedLookups | undefined =
        pendingSalesforceLookupSuggestions != null
          ? salesforceLookupSelections
          : undefined;

      setSyncConflictOpen(false);
      setSearchResult(null);
      setPendingSalesforceMapping(null);
      setPendingSalesforceRows(null);
      setPendingSalesforceLookupSuggestions(null);
      setSalesforceLookupSelections({});
      setPendingSalesforceInputDuplicateGroups(null);
      setSalesforceInputDuplicateSelections({});
      setConflictSelections({});
      setHubspotValidationSummary(null);
      runSalesforceSync(
        pendingSalesforceMapping,
        rowsToSync,
        user_confirmed_selections,
        userConfirmedLookupsForSync
      );
      return;
    }

    if (!pendingMapping || !pendingRows) return;
    const pkCols = pkSourceColumnsFromConnectorFields(pendingMapping.fields);
    const { user_confirmed_selections, rowsToSync } = buildConflictSyncPayload({
      searchResult,
      rows: pendingRows,
      pkSourceColumns: pkCols
    });

    setSyncConflictOpen(false);
    setSearchResult(null);
    setPendingMapping(null);
    setPendingRows(null);
    setConflictSelections({});
    setHubspotValidationSummary(null);
    runSyncWithSelections(
      pendingMapping,
      rowsToSync,
      user_confirmed_selections
    );
  };

  const handleSync = async () => {
    if (!targetObject || !campaignId) return;

    const activeMappings = mappings.filter((m) => m.crmField !== null);
    if (activeMappings.length === 0) {
      showSnackbar(
        'Please map at least one table column to a CRM field',
        'warning'
      );
      return;
    }

    if (!activeMappings.some((m) => m.isPrimaryKey)) {
      showSnackbar(
        'Select at least one primary key column in the mapping to check for existing CRM records and avoid duplicates.',
        'warning'
      );
      return;
    }

    setSyncing(true);
    setSyncProgress(null);
    try {
      const [columnsResponse, { cells }] = await Promise.all([
        getColumns(campaignId, viewType),
        getCells(campaignId, viewType)
      ]);
      const columns: Column[] = (columnsResponse.columns || []).sort(
        (a: Column, b: Column) => (a.col_idx ?? 0) - (b.col_idx ?? 0)
      );
      const columnNames = columns.map((c) => c.col_name);
      let rowIds = Array.from(new Set(cells.map((c) => c.row_id))).sort(
        (a, b) => a - b
      );
      const filteredOut = getFilteredOutRowIds?.();
      if (Array.isArray(filteredOut) && filteredOut.length > 0) {
        const hiddenSet = new Set(filteredOut);
        rowIds = rowIds.filter((id) => !hiddenSet.has(id));
      }
      const cellByKey = new Map<string, string>();
      for (const cell of cells) {
        cellByKey.set(
          `${cell.row_id}\t${cell.column_name}`,
          cell.cell_value ?? ''
        );
      }
      const allRows: Record<string, any>[] = rowIds.map((rowId) => {
        const row: Record<string, any> = {};
        for (const colName of columnNames) {
          row[colName] = cellByKey.get(`${rowId}\t${colName}`) ?? '';
        }
        return row;
      });

      if (allRows.length === 0) {
        showSnackbar(
          'No rows to sync. Add data to the spreadsheet first.',
          'warning'
        );
        setSyncing(false);
        setSyncProgress(null);
        return;
      }

      const sfTargetsWithMeta = activeMappings
        .map((m) => m.crmField)
        .filter((name): name is string => !!name);
      const allowSfPkBlankForReadOnlySearch =
        activeCrm === 'salesforce' &&
        salesforceMappedTargetsIncludeReadOnlySearchField(
          sfTargetsWithMeta,
          crmFields as SalesforceFieldForMapping[]
        );
      const columnIndexByKeyLocal = new Map<string, number>();
      columns.forEach((c, idx) => {
        columnIndexByKeyLocal.set(c.col_name, c.col_idx ?? idx);
      });
      const pkSourceColumnsOrdered = orderedPkSourceColumns(
        activeMappings,
        columnIndexByKeyLocal
      );

      const rowsFiltered = allRows.filter((row) => {
        const hasAllPk = rowHasAllPkValues(
          row as Record<string, unknown>,
          pkSourceColumnsOrdered
        );
        if (hasAllPk) return true;
        // Salesforce may allow single-column blank PKs in special cases.
        if (
          allowSfPkBlankForReadOnlySearch &&
          pkSourceColumnsOrdered.length === 1
        )
          return true;
        return false;
      });
      if (rowsFiltered.length === 0) {
        showSnackbar(
          'No rows to sync. Every primary key column must have data for each row you sync.',
          'warning'
        );
        setSyncing(false);
        setSyncProgress(null);
        return;
      }

      // Map source column -> date/datetime so we can convert Excel serials to CRM format
      const colToDateKind = new Map<string, 'date' | 'datetime'>();
      for (const m of activeMappings) {
        if (!m.crmField) continue;
        const field = crmFields.find((f) => f.apiName === m.crmField);
        if (field?.fieldCategory === FieldCategory.DATE)
          colToDateKind.set(m.tableColumn, 'date');
        else if (field?.fieldCategory === FieldCategory.DATETIME)
          colToDateKind.set(m.tableColumn, 'datetime');
      }
      const rows: Record<string, any>[] = rowsFiltered.map((row) => {
        const out = { ...row };
        Array.from(colToDateKind.entries()).forEach(([col, kind]) => {
          const v = out[col];
          if (v == null || v === '') return;
          if (kind === 'date') out[col] = formatValueForCrmDate(v);
          else out[col] = formatValueForCrmDatetime(v);
        });
        return out;
      });

      if (activeCrm === 'salesforce') {
        const salesforceMapping: SalesforceMappingConfig = {
          target_object: targetObject as SalesforceTargetObjectName,
          fields: activeMappings.map((m, index) => {
            const fieldMeta = crmFields.find(
              (f) => f.apiName === m.crmField
            ) as SalesforceFieldForMapping | undefined;
            const base = {
              source_col: m.tableColumn,
              target_field_api_name: m.crmField!,
              use_for_search: m.isPrimaryKey ?? false,
              normalize: NormalizeRule.NONE,
              order: index,
              allow_overwrite: m.allowOverwrite ?? false
            };
            if (fieldMeta?.fieldCategory === FieldCategory.LOOKUP) {
              return {
                ...base,
                lookup_search_by: m.lookupSearchBy ?? LookupSearchBy.NAME
              };
            }
            return base;
          })
        };
        const { job_id: previewJobId } = await previewSalesforceMappedUpsert({
          mapping: salesforceMapping,
          rows
        });
        const pollIntervalMs = 1500;
        while (true) {
          const status = await getSalesforceMappedUpsertJobStatus(previewJobId);
          if (status.status === 'finished') break;
          if (status.status === 'failed') {
            throw new Error(status.error || 'Preview failed');
          }
          await new Promise((r) => setTimeout(r, pollIntervalMs));
        }
        const previewResult = (await getSalesforceMappedUpsertJobResult(
          previewJobId
        )) as UpsertMappedPreviewResponse;

        const conflicts: SalesforcePreviewConflictResponse = {
          results: previewResult.results,
          not_found: previewResult.not_found
        };

        const hasConflictResults = Object.keys(conflicts.results).length > 0;
        const initialSelections: Record<string, string | null> = {};
        if (hasConflictResults) {
          Object.entries(conflicts.results || {}).forEach(
            ([normalizedKey, matchList]) => {
              const exact = matchList.find((r) => r.exact_match);
              initialSelections[normalizedKey] =
                exact?.id ?? matchList[0]?.id ?? null;
            }
          );
        }

        const inputDupGroupsFiltered = (
          previewResult.input_duplicate_groups ?? []
        ).filter((g) => g.row_indices.length > 1);
        setPendingSalesforceInputDuplicateGroups(
          inputDupGroupsFiltered.length > 0 ? inputDupGroupsFiltered : null
        );
        setSalesforceInputDuplicateSelections(() => {
          const next: Record<string, number> = {};
          for (const g of inputDupGroupsFiltered) {
            const sorted = [...g.row_indices].sort((a, b) => a - b);
            next[g.normalized_primary_key] = sorted[0];
          }
          return next;
        });

        setPendingMapping(null);
        setPendingRows(null);
        setSearchResult(conflicts);
        setConflictSelections(initialSelections);
        setPendingSalesforceMapping(salesforceMapping);
        setPendingSalesforceRows(rows);
        setHubspotValidationSummary(
          (previewResult.validation_summary as SalesforceValidationSummaryWire) ??
            null
        );
        setPendingSalesforceLookupSuggestions(
          previewResult.lookup_suggestions ?? null
        );
        setSalesforceLookupSelections(
          initUserConfirmedLookups(previewResult.lookup_suggestions)
        );
        setSyncConflictOpen(true);
        setSyncing(false);
        return;
      }

      const pkHubSpotPropsOrdered = pkSourceColumnsOrdered.map(
        (col): string => {
          const m = activeMappings.find(
            (x) => x.tableColumn === col && x.isPrimaryKey && x.crmField
          );
          return m!.crmField!;
        }
      );

      if (pkSourceColumnsOrdered.length === 1) {
        const searchValues = Array.from(
          new Set(
            rows
              .map((r) => r[pkSourceColumnsOrdered[0]])
              .filter((v) => v != null && String(v).trim() !== '') as string[]
          )
        ).map((v) => String(v).trim());
        const search_value_rows = searchValues.map((v) => [v]);

        const mapping: MappingConfig = {
          target_object: targetObject as HubSpotTargetObjectName,
          fields: activeMappings.map((m, index) => ({
            source_col: m.tableColumn,
            target_field_api_name: m.crmField!,
            use_for_search: m.isPrimaryKey ?? false,
            order: index,
            allow_overwrite: m.allowOverwrite ?? false
          }))
        };

        hubspotValidationPromiseRef.current = validateHubSpotMapping({
          mapping,
          rows: rowsWithEmptyCellsOmitted(rows)
        });

        const { job_id: searchJobId } = await startHubSpotSearchJob({
          object: targetObject as HubSpotTargetObjectName,
          primary_key_properties: [pkHubSpotPropsOrdered[0]],
          search_value_rows
        });
        await pollJobUntilFinished(searchJobId);
        const searchJobResult = await getHubSpotJobResult(searchJobId);
        const hubSpotSearchResult =
          searchJobResult as unknown as HubSpotSearchResponse;
        const hasConflicts =
          Object.keys(hubSpotSearchResult.results || {}).length > 0;

        const initialSelections: Record<string, string | null> = {};
        if (hasConflicts) {
          Object.entries(hubSpotSearchResult.results || {}).forEach(
            ([normalizedKey, matchList]) => {
              const exact = matchList.find((r) => r.exact_match);
              initialSelections[normalizedKey] =
                exact?.id ?? matchList[0]?.id ?? null;
            }
          );
        }

        setPendingSalesforceMapping(null);
        setPendingSalesforceRows(null);
        setPendingSalesforceLookupSuggestions(null);
        setSalesforceLookupSelections({});
        setPendingSalesforceInputDuplicateGroups(null);
        setSalesforceInputDuplicateSelections({});
        setSearchResult(hubSpotSearchResult);
        setPendingMapping(mapping);
        setPendingRows(rows);
        setConflictSelections(initialSelections);
        setHubspotValidationSummary(null);
        try {
          const validationResponse = await hubspotValidationPromiseRef.current;
          if (validationResponse?.validation_summary) {
            setHubspotValidationSummary(validationResponse.validation_summary);
          }
        } catch (_) {
          // Open modal without validation summary if request failed
        }
        setSyncConflictOpen(true);
        setSyncing(false);
        return;
      }

      const seenTuples = new Set<string>();
      const search_value_rows: string[][] = [];
      for (const row of rows) {
        const nk = normalizedCompositeKeyFromRow(
          row as Record<string, unknown>,
          pkSourceColumnsOrdered
        );
        if (seenTuples.has(nk)) continue;
        seenTuples.add(nk);
        search_value_rows.push(
          pkSourceColumnsOrdered.map((col) => String(row[col] ?? '').trim())
        );
      }

      const mapping: MappingConfig = {
        target_object: targetObject as HubSpotTargetObjectName,
        fields: activeMappings.map((m, index) => ({
          source_col: m.tableColumn,
          target_field_api_name: m.crmField!,
          use_for_search: m.isPrimaryKey ?? false,
          order: index,
          allow_overwrite: m.allowOverwrite ?? false
        }))
      };

      hubspotValidationPromiseRef.current = validateHubSpotMapping({
        mapping,
        rows: rowsWithEmptyCellsOmitted(rows)
      });

      const { job_id: searchJobId } = await startHubSpotSearchJob({
        object: targetObject as HubSpotTargetObjectName,
        primary_key_properties: pkHubSpotPropsOrdered,
        search_value_rows
      });
      await pollJobUntilFinished(searchJobId);
      const searchJobResult = await getHubSpotJobResult(searchJobId);
      const hubSpotSearchResult =
        searchJobResult as unknown as HubSpotSearchResponse;
      const hasConflicts =
        Object.keys(hubSpotSearchResult.results || {}).length > 0;

      const initialSelections: Record<string, string | null> = {};
      if (hasConflicts) {
        Object.entries(hubSpotSearchResult.results || {}).forEach(
          ([normalizedKey, matchList]) => {
            const exact = matchList.find((r) => r.exact_match);
            initialSelections[normalizedKey] =
              exact?.id ?? matchList[0]?.id ?? null;
          }
        );
      }

      setPendingSalesforceMapping(null);
      setPendingSalesforceRows(null);
      setPendingSalesforceLookupSuggestions(null);
      setSalesforceLookupSelections({});
      setPendingSalesforceInputDuplicateGroups(null);
      setSalesforceInputDuplicateSelections({});
      setSearchResult(hubSpotSearchResult);
      setPendingMapping(mapping);
      setPendingRows(rows);
      setConflictSelections(initialSelections);
      setHubspotValidationSummary(null);
      try {
        const validationResponse = await hubspotValidationPromiseRef.current;
        if (validationResponse?.validation_summary) {
          setHubspotValidationSummary(validationResponse.validation_summary);
        }
      } catch (_) {
        // Open modal without validation summary if request failed
      }
      setSyncConflictOpen(true);
      setSyncing(false);
      return;
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to sync to CRM', 'error');
      setSyncing(false);
      setSyncProgress(null);
    }
  };

  const columnIndexByKey = useMemo(() => {
    const map = new Map<string, number>();
    tableColumns.forEach((col, idx) =>
      map.set(col.col_name, col.col_idx ?? idx)
    );
    return map;
  }, [tableColumns]);

  const sortedMappingsView = useMemo(
    () =>
      [...mappings].sort(
        (a, b) =>
          (columnIndexByKey.get(a.tableColumn) ?? 0) -
          (columnIndexByKey.get(b.tableColumn) ?? 0)
      ),
    [mappings, columnIndexByKey]
  );

  const sortedMappingsEdit = useMemo(
    () =>
      [...editingMappings].sort(
        (a, b) =>
          (columnIndexByKey.get(a.tableColumn) ?? 0) -
          (columnIndexByKey.get(b.tableColumn) ?? 0)
      ),
    [editingMappings, columnIndexByKey]
  );

  const salesforceAccountNameMissingInEdit = useMemo(
    () =>
      activeCrm === 'salesforce' &&
      targetObject === SalesforceTargetObjectName.ACCOUNT &&
      !editingMappings.some((m) => m.crmField === 'Name'),
    [activeCrm, targetObject, editingMappings]
  );

  /** `not_found` keys where Account Name is mapped but the cell is blank (creates will fail). */
  const salesforceNotFoundKeysBlankMappedAccountName = useMemo(() => {
    if (
      activeCrm !== 'salesforce' ||
      targetObject !== SalesforceTargetObjectName.ACCOUNT ||
      !mappings.some((m) => m.crmField === 'Name') ||
      !pendingSalesforceMapping ||
      !pendingSalesforceRows?.length ||
      !searchResult ||
      !searchResult.not_found?.length
    ) {
      return [] as string[];
    }
    const nameSrc = mappedSalesforceAccountNameSourceColumn(
      pendingSalesforceMapping
    );
    const pkCols = pkSourceColumnsFromConnectorFields(
      pendingSalesforceMapping.fields
    );
    const out: string[] = [];
    const seen = new Set<string>();
    for (const nk of searchResult.not_found) {
      if (seen.has(nk)) continue;
      const row = pendingSalesforceRows.find(
        (r) =>
          normalizedCompositeKeyFromRow(
            r as Record<string, unknown>,
            pkCols
          ) === nk
      );
      if (!row) continue;
      if (spreadsheetCellIsBlankForCrmSync(row[nameSrc])) {
        seen.add(nk);
        out.push(nk);
      }
    }
    return out;
  }, [
    activeCrm,
    targetObject,
    mappings,
    pendingSalesforceMapping,
    pendingSalesforceRows,
    searchResult
  ]);

  const hasActiveMappings = mappings.some((m) => m.crmField !== null);
  const conflictKeyDisplayMap = useMemo(() => {
    if (pendingSalesforceMapping && pendingSalesforceRows?.length) {
      const pkCols = pkSourceColumnsFromConnectorFields(
        pendingSalesforceMapping.fields
      );
      if (pkCols.length === 0) return undefined;
      if (!pendingSalesforceRows?.length) return undefined;
      const map = new Map<string, string>();
      for (const row of pendingSalesforceRows) {
        const nk = normalizedCompositeKeyFromRow(
          row as Record<string, unknown>,
          pkCols
        );
        map.set(
          nk,
          formatCompositePkDisplay(row as Record<string, unknown>, pkCols)
        );
      }
      return map;
    }
    if (pendingMapping && pendingRows?.length) {
      const pkCols = pkSourceColumnsFromConnectorFields(pendingMapping.fields);
      if (pkCols.length === 0) return undefined;
      if (!pendingRows?.length) return undefined;
      const map = new Map<string, string>();
      for (const row of pendingRows) {
        const nk = normalizedCompositeKeyFromRow(
          row as Record<string, unknown>,
          pkCols
        );
        map.set(
          nk,
          formatCompositePkDisplay(row as Record<string, unknown>, pkCols)
        );
      }
      return map;
    }
    return undefined;
  }, [
    pendingMapping,
    pendingRows,
    pendingSalesforceMapping,
    pendingSalesforceRows
  ]);

  const salesforceInputDuplicateHintFields =
    useMemo((): DuplicateRowHintField[] => {
      if (!pendingSalesforceMapping) return [];
      return [...pendingSalesforceMapping.fields]
        .filter((f) => !f.use_for_search)
        .sort((a, b) => a.order - b.order)
        .map((f) => ({
          sourceCol: f.source_col,
          label: displayColumnName(f.source_col)
        }));
    }, [pendingSalesforceMapping]);

  if (loadingColumns) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasCrmConnected) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <CRMMappingHeader
          onEdit={() => {}}
          showEditButton={false}
          actionButtons={[
            <IconButton
              key="close"
              size="small"
              onClick={onClose}
              aria-label="Close page"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ]}
        />
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          sx={{ py: 6, px: 2, flex: 1 }}
        >
          {isAdmin ? (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, textAlign: 'center' }}
              >
                No CRM connected. Connect Salesforce or HubSpot to sync your
                data.
              </Typography>
              <Button
                component={RouterLink}
                to={`${URL_PATHNAMES.SETTINGS}?tab=integrations`}
                variant="contained"
                startIcon={<SettingsIcon />}
                onClick={onClose}
                sx={(theme) => ({
                  border: `1px solid ${theme.customColors?.themePrimary || 'primary.main'}`,
                  backgroundColor:
                    theme.customColors?.themePrimary || 'primary.main',
                  color: theme.customColors?.themeSecondary || 'white',
                  borderRadius: '50px',
                  textTransform: 'capitalize',
                  px: 4,
                  py: 1.5
                })}
              >
                Go to Settings to Integrate
              </Button>
            </>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', maxWidth: 320 }}
            >
              No CRM connected. Contact your admin to integrate Salesforce or
              HubSpot.
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <CRMMappingHeader
        onEdit={handleEdit}
        showEditButton={!mappingEditModalOpen}
        actionButtons={[
          <IconButton
            key="close"
            size="small"
            onClick={onClose}
            aria-label="Close page"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ]}
      />

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {bothCrmConnected && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Sync to
            </Typography>
            <ToggleButtonGroup
              value={activeCrm}
              exclusive
              onChange={(_, value: CrmProvider | null) =>
                value != null && setSelectedCrm(value)
              }
              size="small"
            >
              <ToggleButton value="salesforce" aria-label="Salesforce">
                Salesforce
              </ToggleButton>
              <ToggleButton value="hubspot" aria-label="HubSpot">
                HubSpot
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
        <TargetObjectSelector
          crm={activeCrm}
          campaignType={campaignType}
          viewType={viewType}
          targetObject={targetObject}
          isEditMode={false}
          onTargetObjectChange={handleTargetObjectChange}
        />

        {loadingCrmFields && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        )}

        {targetObject && !loadingCrmFields && (
          <MappingTable
            mappings={sortedMappingsView}
            crmFields={crmFields}
            isEditMode={false}
            activeCrm={activeCrm}
            onMappingChange={handleMappingChange}
            onPrimaryKeyChange={handlePrimaryKeyChange}
            onAllowOverwriteChange={handleAllowOverwriteChange}
            onLookupSearchByChange={handleLookupSearchByChange}
          />
        )}

        {!targetObject && <EmptyState onCreateMapping={handleEdit} />}
      </Box>

      {targetObject && (
        <SyncButton
          syncing={syncing}
          disabled={!hasActiveMappings || syncing || loadingCrmFields}
          onSync={handleSync}
          progress={syncProgress}
        />
      )}

      <Dialog
        open={mappingEditModalOpen}
        onClose={() => {
          if (saving) return;
          handleMappingEditModalClose();
        }}
        maxWidth="md"
        fullWidth
        aria-labelledby="crm-mapping-edit-dialog-title"
      >
        <DialogTitle id="crm-mapping-edit-dialog-title">
          Edit CRM mapping
        </DialogTitle>
        <Divider />
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', minHeight: 360 }}
        >
          {bothCrmConnected && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Sync to
              </Typography>
              <ToggleButtonGroup
                value={activeCrm}
                exclusive
                onChange={(_, value: CrmProvider | null) =>
                  value != null && setSelectedCrm(value)
                }
                size="small"
              >
                <ToggleButton value="salesforce" aria-label="Salesforce">
                  Salesforce
                </ToggleButton>
                <ToggleButton value="hubspot" aria-label="HubSpot">
                  HubSpot
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
          <TargetObjectSelector
            crm={activeCrm}
            campaignType={campaignType}
            viewType={viewType}
            targetObject={targetObject}
            isEditMode
            onTargetObjectChange={handleTargetObjectChange}
          />
          {loadingCrmFields ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            targetObject && (
              <>
                <MappingTable
                  mappings={sortedMappingsEdit}
                  crmFields={crmFields}
                  isEditMode
                  activeCrm={activeCrm}
                  onMappingChange={handleMappingChange}
                  onPrimaryKeyChange={handlePrimaryKeyChange}
                  onAllowOverwriteChange={handleAllowOverwriteChange}
                  onLookupSearchByChange={handleLookupSearchByChange}
                />
                <Stack spacing={2} sx={{ mt: 2, alignItems: 'stretch' }}>
                  {salesforceAccountNameMissingInEdit && (
                    <Alert severity="warning" sx={{ flex: 1 }}>
                      Account Name is not mapped. You will not be able to create
                      new Account records in Salesforce until you map the{' '}
                      <strong>Account Name</strong> field to a column.
                    </Alert>
                  )}
                  <Alert
                    severity="info"
                    sx={{
                      flex: salesforceAccountNameMissingInEdit ? 1 : undefined
                    }}
                  >
                    Map your table columns to CRM fields. Select a primary key
                    field and configure overwrite settings.
                  </Alert>
                </Stack>
                <EditModeActions
                  editingMappings={editingMappings}
                  saving={saving}
                  onCancel={handleCancel}
                  onSave={() => void handleSave()}
                />
              </>
            )
          )}
        </DialogContent>
      </Dialog>

      <SyncConflictModal
        open={syncConflictOpen}
        crm={activeCrm}
        salesforceTargetIsAccount={
          activeCrm === 'salesforce' &&
          targetObject === SalesforceTargetObjectName.ACCOUNT
        }
        salesforceAccountNameMapped={mappings.some(
          (m) => m.crmField === 'Name'
        )}
        salesforceNotFoundKeysBlankMappedAccountName={
          salesforceNotFoundKeysBlankMappedAccountName
        }
        searchResults={searchResult}
        userSelections={conflictSelections}
        keyDisplayMap={conflictKeyDisplayMap}
        onSelectionChange={(normalizedKey, crmRecordId) => {
          setConflictSelections((prev) => ({
            ...prev,
            [normalizedKey]: crmRecordId
          }));
        }}
        onConfirm={handleConflictConfirm}
        onCancel={() => {
          setSyncConflictOpen(false);
          setSearchResult(null);
          setPendingMapping(null);
          setPendingRows(null);
          setPendingSalesforceMapping(null);
          setPendingSalesforceRows(null);
          setPendingSalesforceLookupSuggestions(null);
          setSalesforceLookupSelections({});
          setPendingSalesforceInputDuplicateGroups(null);
          setSalesforceInputDuplicateSelections({});
          setConflictSelections({});
          setHubspotValidationSummary(null);
        }}
        confirmLabel={
          (searchResult &&
            Object.keys(searchResult.results || {}).length > 0) ||
          (activeCrm === 'salesforce' &&
            !!pendingSalesforceInputDuplicateGroups?.some(
              (g) => g.row_indices.length > 1
            )) ||
          (activeCrm === 'salesforce' &&
            pendingSalesforceLookupSuggestions != null &&
            Object.keys(pendingSalesforceLookupSuggestions).length > 0)
            ? 'Sync with choices'
            : 'Confirm sync'
        }
        inputDuplicateGroups={pendingSalesforceInputDuplicateGroups}
        inputDuplicateSelections={salesforceInputDuplicateSelections}
        inputDuplicatePreviewRows={pendingSalesforceRows ?? null}
        inputDuplicateHintFields={salesforceInputDuplicateHintFields}
        onInputDuplicateSelectionChange={
          activeCrm === 'salesforce'
            ? (normalizedPrimaryKey, spreadsheetRowIndex) => {
                setSalesforceInputDuplicateSelections((prev) => ({
                  ...prev,
                  [normalizedPrimaryKey]: spreadsheetRowIndex
                }));
              }
            : undefined
        }
        disabled={syncing}
        validationSummary={hubspotValidationSummary}
        crmFieldTargetsForValidation={
          crmFields as HubSpotFieldForMapping[] | SalesforceFieldForMapping[]
        }
        lookupSuggestions={pendingSalesforceLookupSuggestions ?? undefined}
        lookupSelections={salesforceLookupSelections}
        onLookupSelectionChange={
          activeCrm === 'salesforce'
            ? (fieldApiName, sourceValue, salesforceId) => {
                setSalesforceLookupSelections((prev) => ({
                  ...prev,
                  [fieldApiName]: {
                    ...(prev[fieldApiName] || {}),
                    [sourceValue]: salesforceId
                  }
                }));
              }
            : undefined
        }
      />
    </Box>
  );
};

export default CRMMapping;
